import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Send, Sparkles, Loader2 } from "lucide-react";

export default function ChatThread() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [openers, setOpeners] = useState([]);
  const scrollRef = useRef(null);

  const suggestOpeners = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/ice-breakers", { other_user_id: userId });
      setOpeners(data.openers || []);
    } catch (err) {
      console.error("[AI] openers failed", err);
      toast.error(err.response?.data?.detail || "AI failed");
    } finally {
      setAiLoading(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/messages/thread/${userId}`);
      setMessages(data.messages);
      setOther(data.other_user);
    } catch (err) {
      console.error("[ChatThread] failed to load thread", err);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [userId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    const optimistic = { id: `tmp-${Date.now()}`, from_user_id: user.id, to_user_id: userId, text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    const payload = text;
    setText("");
    try {
      await api.post("/messages", { to_user_id: userId, text: payload });
      await load();
    } catch {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex flex-col">
      <TopBar />
      <div className="fixed top-14 left-0 right-0 z-30 bg-[#0B0F17]/85 backdrop-blur-md px-4 py-2 border-b border-white/5 flex items-center gap-3">
        <button onClick={() => navigate("/chats")} data-testid="chat-back" className="text-slate-400 hover:text-white">
          <ArrowLeft size={18} />
        </button>
        {other && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center text-xs font-bold overflow-hidden">
              {other.photo_url ? <img src={other.photo_url} alt="" className="w-full h-full object-cover" /> : other.name?.[0]?.toUpperCase()}
            </div>
            <div className="font-semibold text-sm" data-testid="chat-other-name">{other.name}</div>
          </div>
        )}
      </div>

      <main
        ref={scrollRef}
        className="flex-1 overflow-y-auto pt-24 px-4 pb-24 max-w-2xl w-full mx-auto"
      >
        {messages.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-16" data-testid="chat-empty">
            Say something to start the conversation.
          </div>
        ) : (
          <div className="space-y-2">
            {messages.map((m) => {
              const mine = m.from_user_id === user.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    mine ? "bg-rose-500 text-white rounded-br-sm" : "bg-[#131A26] border border-white/10 rounded-bl-sm"
                  }`}>
                    {m.text}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <form
        onSubmit={send}
        className="fixed bottom-0 left-0 right-0 z-40 p-3 bg-[#0B0F17]/95 backdrop-blur-xl border-t border-white/10"
        data-testid="chat-form"
      >
        <div className="max-w-2xl mx-auto">
          {openers.length > 0 && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="ai-openers">
              {openers.map((o, i) => (
                <button
                  key={`opener-${i}-${o.slice(0, 20)}`}
                  type="button"
                  onClick={() => { setText(o); setOpeners([]); }}
                  data-testid={`ai-opener-${i}`}
                  className="shrink-0 max-w-[70%] px-3 py-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-xs text-amber-100 text-left hover:bg-amber-500/25"
                >
                  {o}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setOpeners([])}
                className="shrink-0 px-2 text-xs text-slate-500 hover:text-rose-400"
              >
                ×
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={suggestOpeners}
              disabled={aiLoading || messages.length > 2}
              data-testid="ai-suggest-opener"
              title={messages.length > 2 ? "Only for new conversations" : "AI opener"}
              className="h-11 px-3 rounded-xl bg-gradient-to-br from-rose-500/20 to-amber-500/20 border border-amber-500/30 text-amber-200 hover:text-white disabled:opacity-40 inline-flex items-center justify-center"
            >
              {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            </button>
            <Input
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 h-11 bg-[#131A26] border-white/10"
            />
            <Button type="submit" disabled={busy || !text.trim()} data-testid="chat-send" className="h-11 px-5 bg-rose-500 hover:bg-rose-400 rounded-xl">
              <Send size={16} />
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
